import { createSlice } from '@reduxjs/toolkit';
import { endOfDay, startOfDay } from '../../dashboard/utils/datetimeUtils';
import { isLocalhost } from '../../dashboard/utils/utils';

/**
 * Helpers: allow positive (+future) and negative (-past) offsets
 */
const getBeginningDate = (dayOffset = 0) => {
  const date = new Date();
  date.setDate(date.getDate() + dayOffset);
  return startOfDay(date);
};

const getEndDate = (dayOffset = 0) => {
  const date = new Date();
  date.setDate(date.getDate() + dayOffset);
  return endOfDay(date);
};

/**
 * Config: change offsets here anytime
 * Examples:
 *  0  = today
 * -1  = yesterday
 * -2  = 2 days before
 *  1  = tomorrow
 */
const DATE_CONFIG = {
  beginningOffset: -1, // default: yesterday
  endOffset: 1,        // default: tomorrow
};

const initialState = {
  data: [],
  status: 'idle',
  error: null,
  beginingDate: getBeginningDate(DATE_CONFIG.beginningOffset).toISOString(),
  endDate: getEndDate(DATE_CONFIG.endOffset).toISOString(),
};

const dataSlice = createSlice({
  name: 'uidata',
  initialState,
  reducers: {
    // Add new data
    addData: (state, action) => {
      state.data.push(action.payload);
    },

    // Update existing data by ID
    updateData: (state, action) => {
      const { id, updates } = action.payload;
      const index = state.data.findIndex((item) => item.id === id);
      if (index !== -1) {
        state.data[index] = { ...state.data[index], ...updates };
      }
    },

    // Delete data by ID
    deleteData: (state, action) => {
      const id = action.payload;
      state.data = state.data.filter((item) => item.id !== id);
    },

    // Set data directly (e.g., after fetching from an API)
    setData: (state, action) => {
      state.data = action.payload;
    },

    // Clear all data (fixed bug)
    clearData: (state) => {
      state.data = [];
    },

    // Manually update dates
    updateBeginingDate: (state, action) => {
      state.beginingDate = action.payload;
    },

    updateEndDate: (state, action) => {
      state.endDate = action.payload;
    },

    /**
     * NEW: Set date range using offsets
     * Example:
     * dispatch(setDateRangeByOffset({ beginningOffset: -2, endOffset: 0 }))
     */
    setDateRangeByOffset: (state, action) => {
      const { beginningOffset = 0, endOffset = 0 } = action.payload;

      state.beginingDate = getBeginningDate(beginningOffset).toISOString();
      state.endDate = getEndDate(endOffset).toISOString();
    },
  },
});

export const {
  addData,
  updateData,
  deleteData,
  setData,
  clearData,
  updateBeginingDate,
  updateEndDate,
  setDateRangeByOffset,
} = dataSlice.actions;

/**
 * Selectors
 */
export const selectData = (state) => state.uidata.data;
export const selectStatus = (state) => state.uidata.status;
export const selectError = (state) => state.uidata.error;

export const selectQueryDate = (state) => {
  return {
    beginingDate:
      state?.uidata?.beginingDate ||
      getBeginningDate(DATE_CONFIG.beginningOffset).toISOString(),
    endDate:
      state?.uidata?.endDate ||
      getEndDate(DATE_CONFIG.endOffset).toISOString(),
  };
};

export default dataSlice.reducer;